import React, {useCallback, useEffect, useState} from "react";

import './DevicesTable.scss'
import {useRecoilValue} from "recoil";
import {grpcDeviceServiceSelector} from "../../state/grpc";
import {
  Button,
  DataTable,
  DataTableSkeleton,
  OverflowMenu, OverflowMenuItem, Pagination, Table,
  TableBody, TableCell,
  TableContainer, TableHead, TableHeader, TableRow, TableSelectAll, TableSelectRow,
  TableToolbar, TableToolbarContent, TableToolbarSearch
} from "@carbon/react";
import PropTypes from "prop-types";
import {useNavigate} from "react-router-dom";
import {ADD, useToastContext} from "../Toast";
import ConfirmDialog from "../ConfirmDialog";

const DevicesTable = (props) => {
  const systemCode = props.systemCode
  const headers = [{
    key: 'id', header: 'ID'
  }, {
    key: 'code', header: 'Codice strumento'
  }, {
    key: 'customerCode', header: 'Codice cliente'
  }, {
    key: 'suspended', header: 'Sospeso'
  }, {
    key: 'h24Disabled', header: 'H24 Disabilitato'
  }];

  const navigate = useNavigate();
  const {toastDispatch} = useToastContext();
  const deviceService = useRecoilValue(grpcDeviceServiceSelector)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [firstRowIndex, setFirstRowIndex] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)
  const [deleteModalId, setDeleteModalId] = useState(null)

  const navigateToAdd = useCallback((systemCode) => {
    navigate('/customers/' + systemCode + '/devices/add')
  }, [navigate])

  const navigateToEdit = useCallback((systemCode, deviceId) => {
    navigate('/customers/' + systemCode + '/devices/' + deviceId)
  }, [navigate])

  const getDevices = () => {
    const req = new proto.api.ListDevicesRequest()
    req.setCustomerCode(systemCode)
    setLoading(true)
    deviceService.listDevices(req, null, (err, resp) => {
      if (err != null) {
        console.error(err)
        setError(true)
      } else {
        setDevices(resp.toObject().devicesList)
        setError(false)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    getDevices()
  }, [])

  if (loading) {
    return (
      <DataTableSkeleton
        columnCount={headers.length + 1}
        rowCount={5}
        headers={headers}
      />
    )
  }

  if (error) {
    return (
      <span>ERROR!</span>
    )
  }

  const handlePaginationChange = (page, newPageSize) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    setFirstRowIndex(newPageSize * (page - 1));
  }

  const processChildren = (cell) => {
    let value = cell.value
    switch (cell.info.header) {
      case "suspended": {
        value = cell.value ? "Si" : "No"
        break
      }
      case "h24Disabled": {
        value = cell.value ? "Si" : "No"
        break
      }
    }
    return (<>
      {value}
    </>)
  }

  return (
    <DataTable
      rows={devices}
      headers={headers}
      isSortable
      overflowMenuOnHover={false}
      render={(
        {
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getSelectionProps,
          getToolbarProps,
          onInputChange,
          getTableProps,
          getTableContainerProps,
        }) => {
        return (
          <TableContainer title="Lista strumenti"
                          description="Lista degli strumenti configurati" {...getTableContainerProps()}>
            <ConfirmDialog
              openState={deleteModalOpened}
              setOpenState={setDeleteModalOpened}
              heading={"Sei sicuro di voler resettare lo strumento?"}
              primaryButtonText="Resetta"
              onRequestSubmit={() => {
                const req = new proto.api.DeleteDeviceRequest()
                req.setId(deleteModalId)
                deviceService.deleteDevice(req, null, (err) => {
                  if (err != null) {
                    console.error(err)
                    toastDispatch({
                      type: ADD,
                      payload: {
                        title: "Operazione fallita",
                        content: "Impossibile resettare lo strumento",
                        kind: "error",
                        caption: err.message
                      }
                    })
                  } else {
                    getDevices()
                    setDeleteModalOpened(false)
                    toastDispatch({
                      type: ADD,
                      payload: {
                        title: "Operazione avvenuta con successo",
                        content: "Strumento resettato con successo",
                        kind: "success"
                      }
                    })
                  }
                })
              }}
            />
            <TableToolbar {...getToolbarProps()} >
              <TableToolbarContent>
                <TableToolbarSearch onChange={onInputChange} size="lg"/>
                <Button onClick={() => navigateToAdd(systemCode)}>Nuovo strumento</Button>
              </TableToolbarContent>
            </TableToolbar>
            <Pagination
              totalItems={rows.length}
              backwardText="Precedente"
              forwardText="Successiva"
              pageSize={25}
              pageSizes={[10, 25, 50, 100]}
              itemsPerPageText="Elementi per pagina"
              pageRangeText={(_current, total) => `di ${total} ${total === 1 ? 'pagina' : 'pagine'}`}
              itemRangeText={(min, max, total) => `${min}–${max} of ${total} elementi`}
              onChange={({page, pageSize}) => handlePaginationChange(page, pageSize)}
            />
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  <TableSelectAll {...getSelectionProps()} />
                  {headers.map((header) => (<TableHeader {...getHeaderProps({header})}>
                    {header.header}
                  </TableHeader>))}
                  <TableHeader style={{width: "1rem"}} scope="col"/>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .slice(firstRowIndex, firstRowIndex + pageSize)
                  .map((row) => (
                    <TableRow key={row.id} {...getRowProps({row})}>
                      <TableSelectRow {...getSelectionProps({row})} />
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>
                          {processChildren(cell)}
                        </TableCell>
                      ))}
                      <TableCell className="cds--table-column-menu">
                        <OverflowMenu flipped size='md' aria-label="Opzioni" iconDescription="Opzioni">
                          <OverflowMenuItem itemText="Configura" onClick={() => navigateToEdit(systemCode, row.id)}/>
                          <OverflowMenuItem
                            isDelete itemText="Resetta"
                            onClick={() => {
                              setDeleteModalId(row.id)
                              setDeleteModalOpened(true)
                            }}/>
                        </OverflowMenu>
                      </TableCell>
                    </TableRow>))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }}
    />
  )
}

DevicesTable.propTypes = {
  systemCode: PropTypes.string.isRequired,
}

export default DevicesTable